import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { ChipsSeleccion, Icon, TarjetasOpcion, type OpcionTarjeta } from "@/components/ds";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import { cleanRut, formatRut } from "@/lib/rut";
import type {
  DestinatarioPersona,
  DestinatariosOpciones,
  Programa,
  ProgramaTematica,
  TematicaDestinatarioRef,
  TematicaDestinatarioTipo,
} from "@/types/capacitaciones-ia";

function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.includes("T") ? iso : `${iso}T00:00:00`).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Cabecera de un bloque del drawer: título + regla que ocupa el resto del
 * ancho. Mismo patrón que `NuevoProgramaDrawer` — dos preguntas ("de qué
 * programa sale", "a quién le llega") en vez de una lista de cajas sueltas.
 */
function SeccionDrawer({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2.5">
        <h3 className="text-sm font-semibold tracking-[-0.01em]">{titulo}</h3>
        <div className="bg-border h-px flex-1" />
      </div>
      {children}
    </section>
  );
}

/**
 * A quién le llega el correo de una temática que ya venía programada.
 *
 * Espeja lo que hace el backend en `iniciarEnvio()`: sin `destinatario_tipo`
 * el alcance cae a toda la empresa y `destinatario_refs` se ignora. Los
 * nombres salen del padrón que la pantalla ya cargó, sin pedirle nada nuevo
 * al backend. Una ref colgante (cargo o área borrada) simplemente no
 * aparece, mismo criterio que el resto de la pantalla.
 */
function etiquetaDestinatarios(
  tema: ProgramaTematica,
  opciones: DestinatariosOpciones | null
): string {
  const tipo = tema.destinatario_tipo;
  if (tipo === null || tipo === "empresa") {
    return "todos los trabajadores activos con correo registrado";
  }
  if (tipo === "persona") return "los trabajadores elegidos para esta temática";

  const catalogo = tipo === "cargo" ? (opciones?.cargos ?? []) : (opciones?.areas ?? []);
  const refs = tema.destinatario_refs ?? [];
  const nombres = catalogo.filter((o) => refs.includes(o.id)).map((o) => o.nombre);
  const alcance = tipo === "cargo" ? "los cargos" : "las áreas";
  return nombres.length > 0
    ? `los trabajadores de ${alcance}: ${nombres.join(", ")}`
    : `los trabajadores de ${alcance} elegidas para esta temática`;
}

/** Glifo que acompaña al alcance heredado — el mismo que su tarjeta en el
 *  selector, para que el bloque de sólo lectura y el editable se lean como
 *  dos caras de lo mismo. */
function iconoAlcance(tipo: TematicaDestinatarioTipo | null): string {
  if (tipo === "cargo") return "badge";
  if (tipo === "area") return "location_on";
  if (tipo === "persona") return "person";
  return "domain";
}

export interface EnviarAhoraDrawerProps {
  /** Temática a enviar — `null` cierra el drawer. */
  tema: ProgramaTematica | null;
  /** El Programa al que pertenece `tema`: da el nombre y el conteo de temáticas. */
  programa: Programa | null;
  /** Padrón real para "Llega a N" — la carga la página una sola vez. */
  opciones: DestinatariosOpciones | null;
  enviando: boolean;
  onCerrar: () => void;
  /** El alcance viaja sólo cuando la temática NO estaba programada: una
   *  programada ya tiene audiencia congelada y adelantarla no la cambia. */
  onConfirmar: (alcance: { tipo: TematicaDestinatarioTipo; refs: TematicaDestinatarioRef[] } | null) => void;
}

/**
 * Drawer "Enviar ahora" — una temática sale hoy, fuera del calendario.
 *
 * Tiene DOS estados, decididos por la temática y no por quien lo abre:
 *
 *  - **Ya programada**: la audiencia se definió al programar la serie y
 *    adelantar el envío no la cambia. El bloque de destinatarios es de sólo
 *    lectura; el backend (`enviar-ahora`) ni siquiera acepta uno nuevo.
 *  - **Sin programación**: nunca pasó por "Programar envíos", así que no hay
 *    audiencia que respetar y elegirla es parte del acto de enviar. El
 *    alcance elegido viaja en el request y el backend lo persiste antes de
 *    disparar (`EnviarCapacitacionRequest`).
 *
 * Antes esto era un `AlertDialog` que en el segundo caso sólo ANUNCIABA a
 * quién le iba a llegar —siempre la dotación completa— sin dejar corregirlo.
 */
export function EnviarAhoraDrawer({
  tema,
  programa,
  opciones,
  enviando,
  onCerrar,
  onConfirmar,
}: EnviarAhoraDrawerProps) {
  const [destinatarioTipo, setDestinatarioTipo] = useState<TematicaDestinatarioTipo>("empresa");
  const [destinatarioRefs, setDestinatarioRefs] = useState<TematicaDestinatarioRef[]>([]);
  const [verExcluidos, setVerExcluidos] = useState(false);
  const [personas, setPersonas] = useState<DestinatarioPersona[]>([]);
  const [cargandoPersonas, setCargandoPersonas] = useState(false);

  // Una temática programada trae su audiencia congelada: el selector no se
  // dibuja y lo que se muestra sale de la temática, no del estado local.
  const heredada = tema?.estado_negocio === "programada";

  useEffect(() => {
    if (!tema) return;
    // Cada apertura arranca limpia: el alcance elegido para una temática no
    // puede quedar pegado y salir sin querer en la siguiente.
    setDestinatarioTipo(tema.destinatario_tipo ?? "empresa");
    setDestinatarioRefs(tema.destinatario_refs ?? []);
    setVerExcluidos(false);
  }, [tema]);

  useEffect(() => {
    if (destinatarioTipo !== "persona" || personas.length > 0 || cargandoPersonas) return;
    setCargandoPersonas(true);
    // Endpoint de capacitaciones, NO `/personas?all=1`: ese es el listado de
    // RRHH e incluye inactivos a propósito. El filtro de elegibilidad vive en
    // el backend (DestinatariosService) y NO se reimplementa acá.
    capacitacionesIaApi
      .getDestinatariosPersonas()
      .then((res) => setPersonas(res.data))
      .catch(() => setPersonas([]))
      .finally(() => setCargandoPersonas(false));
  }, [destinatarioTipo, personas.length, cargandoPersonas]);

  const enviadas = programa
    ? programa.temas.filter((t) => t.estado_negocio === "enviada").length
    : 0;
  const yaProgramadas = programa
    ? programa.temas.filter((t) => t.estado_negocio === "programada").length
    : 0;
  const pendientes = programa
    ? programa.temas.filter((t) => t.estado_negocio === "pendiente").length
    : 0;

  const resumenTem = programa
    ? `${programa.temas.length} temáticas en total · ${enviadas} ya enviadas · ${yaProgramadas} ya programadas · ${
        pendientes > 0 ? `${pendientes} por programar` : "ninguna por programar"
      }`
    : "";

  // Alcance efectivo: el congelado de la temática programada, o el que se está
  // eligiendo. Un solo cálculo alimenta el contador, el pie y el resumen, así
  // que los dos estados del drawer no pueden mostrar números de fuentes
  // distintas.
  const nDestinatarios = useMemo(() => {
    if (!opciones) return 0;
    const tipo: TematicaDestinatarioTipo = heredada
      ? (tema?.destinatario_tipo ?? "empresa")
      : destinatarioTipo;
    const refs: TematicaDestinatarioRef[] = heredada
      ? (tema?.destinatario_refs ?? [])
      : destinatarioRefs;

    if (tipo === "empresa") return opciones.dotacion;
    if (tipo === "cargo") {
      return opciones.cargos
        .filter((c) => refs.includes(c.id))
        .reduce((acc, c) => acc + c.conteo, 0);
    }
    if (tipo === "area") {
      return opciones.areas
        .filter((a) => refs.includes(a.id))
        .reduce((acc, a) => acc + a.conteo, 0);
    }
    return refs.length; // persona: 1 seleccionado = 1 destinatario
  }, [opciones, heredada, tema, destinatarioTipo, destinatarioRefs]);

  const excluidos = opciones?.excluidos ?? null;
  const hayExcluidos = (excluidos?.total ?? 0) > 0;

  // "1 de 2 trabajadores" sólo cuando hay diferencia que explicar. Sin
  // excluidos, un "2 de 2" es ruido: la tarjeta vuelve a decir "2 trabajadores".
  const descripcionEmpresa = !opciones
    ? "— trabajadores"
    : hayExcluidos
      ? `${opciones.dotacion} de ${opciones.dotacion_total} trabajadores`
      : `${opciones.dotacion} ${opciones.dotacion === 1 ? "trabajador" : "trabajadores"}`;

  const tarjetasDestino: OpcionTarjeta<TematicaDestinatarioTipo>[] = [
    { valor: "empresa", titulo: "Toda la empresa", icono: "domain", descripcion: descripcionEmpresa },
    { valor: "cargo", titulo: "Por cargo", icono: "badge", descripcion: `${opciones?.cargos.length ?? 0} cargos` },
    ...(opciones && opciones.areas.length > 0
      ? ([{ valor: "area", titulo: "Por área", icono: "location_on", descripcion: `${opciones.areas.length} áreas` }] as OpcionTarjeta<TematicaDestinatarioTipo>[])
      : []),
    { valor: "persona", titulo: "Trabajador específico", icono: "person", descripcion: "Busca por nombre o RUT" },
  ];

  const preguntas = tema?.preguntas_por_tematica ?? 0;

  const resumenEnvio = heredada
    ? `Se genera con IA y el correo sale de inmediato a los mismos destinatarios que elegiste al programarla. La IA genera ${preguntas} preguntas.`
    : `Se genera con IA y el correo sale de inmediato a ${nDestinatarios} ${
        nDestinatarios === 1 ? "trabajador" : "trabajadores"
      }. La IA genera ${preguntas} preguntas.`;

  const aviso = heredada
    ? "Ese día queda libre: la temática sale de la programación y no vuelve a enviarse sola. El correo no se puede recoger."
    : "No entra en el calendario ni mueve al resto del programa: sale sólo esta vez. El correo no se puede recoger.";

  function elegirTipo(tipo: TematicaDestinatarioTipo) {
    setDestinatarioTipo(tipo);
    setDestinatarioRefs([]);
  }

  function confirmar() {
    onConfirmar(heredada ? null : { tipo: destinatarioTipo, refs: destinatarioRefs });
  }

  return (
    <Sheet
      open={tema !== null}
      onOpenChange={(v) => !v && !enviando && onCerrar()}
    >
      <SheetContent className="sm:w-[620px] sm:max-w-[96vw]">
        <SheetHeader>
          <SheetTitle>Enviar ahora «{tema?.titulo}»</SheetTitle>
          <SheetDescription>
            {heredada
              ? `Está programada para el ${formatFecha(tema?.fecha_programada ?? null)}.`
              : "No tiene día reservado."}
          </SheetDescription>
        </SheetHeader>

        {/* `SheetBody` y no un div a mano: trae el `min-h-0` que hace que
            scrollee SÓLO el cuerpo y el padding de 18px que alinea con los
            divisores del encabezado y del pie. El gap sube a 22px — es la
            separación entre secciones del diseño, más aire que el default. */}
        <SheetBody className="gap-[22px]">
          <SeccionDrawer titulo="Programa">
            <div className="bg-surface-muted border-border-subtle flex items-center gap-2.5 rounded-lg border px-[13px] py-[11px]">
              <Icon name="school" size={18} className="text-icon-soft shrink-0" />
              <span className="min-w-0 flex-1 text-[13px] font-medium">
                {programa?.nombre ?? "Programa sin nombre"}
              </span>
            </div>
            <div className="border-border-subtle flex items-center gap-2 rounded-lg border px-3 py-2">
              <Icon name="playlist_add_check" size={16} className="text-icon-soft shrink-0" />
              <span className="text-[11.5px] leading-[1.45] text-muted-foreground">{resumenTem}</span>
            </div>
          </SeccionDrawer>

          {/* El envío es irreversible y se sale del calendario: eso va en ámbar
              arriba de todo, no escondido en el resumen del pie. */}
          <div className="border-warning/25 bg-warning-bg flex items-start gap-2.5 rounded-lg border p-3">
            <Icon name="bolt" size={18} className="text-warning mt-px shrink-0" />
            <p className="text-warning text-[11.5px] leading-[1.5]">{aviso}</p>
          </div>

          <SeccionDrawer titulo="Destinatarios">
            {heredada ? (
              <>
                <p className="text-soft text-[11.5px] leading-[1.45]">
                  Los mismos que elegiste al programarla.
                </p>
                <div className="bg-surface-muted border-border-subtle flex items-center gap-[11px] rounded-lg border px-[13px] py-3">
                  <Icon
                    name={iconoAlcance(tema?.destinatario_tipo ?? null)}
                    size={19}
                    className="text-icon-soft shrink-0"
                  />
                  <span className="min-w-0 flex-1 text-[12.5px] leading-[1.4]">
                    {tema ? etiquetaDestinatarios(tema, opciones) : ""}
                  </span>
                  <span className="fc-num shrink-0 text-[13px] font-medium">{nDestinatarios}</span>
                </div>
              </>
            ) : (
              <>
                <p className="text-soft text-[11.5px]">
                  Elige el alcance. El contador se actualiza en vivo.
                </p>
                <TarjetasOpcion<TematicaDestinatarioTipo>
                  opciones={tarjetasDestino}
                  valor={destinatarioTipo}
                  onCambiar={elegirTipo}
                  columnas={2}
                  disabled={enviando}
                />

                {destinatarioTipo === "cargo" && opciones && opciones.cargos.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <Label className="text-[12.5px]">Cargos</Label>
                      <span className="fc-num text-soft text-[11px]">
                        {destinatarioRefs.length} seleccionados
                      </span>
                    </div>
                    <ChipsSeleccion
                      opciones={opciones.cargos.map((c) => ({
                        id: c.id,
                        etiqueta: c.nombre,
                        conteo: c.conteo,
                      }))}
                      seleccionados={destinatarioRefs as number[]}
                      onCambiar={(ids) => setDestinatarioRefs(ids)}
                      disabled={enviando}
                      aria-label="Cargos destinatarios"
                    />
                  </div>
                )}

                {destinatarioTipo === "area" && opciones && opciones.areas.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <Label className="text-[12.5px]">Áreas</Label>
                      <span className="fc-num text-soft text-[11px]">
                        {destinatarioRefs.length} seleccionadas
                      </span>
                    </div>
                    <ChipsSeleccion
                      opciones={opciones.areas.map((a) => ({
                        id: a.id,
                        etiqueta: a.nombre,
                        conteo: a.conteo,
                      }))}
                      seleccionados={destinatarioRefs as number[]}
                      onCambiar={(ids) => setDestinatarioRefs(ids)}
                      disabled={enviando}
                      aria-label="Áreas destinatarias"
                    />
                  </div>
                )}

                {destinatarioTipo === "persona" && (
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <Label className="text-[12.5px]">Trabajadores</Label>
                      <span className="fc-num text-soft text-[11px]">
                        {destinatarioRefs.length} seleccionados
                      </span>
                    </div>
                    <MultiSelect
                      options={personas.map((p) => ({
                        value: p.uuid,
                        label: `${p.nombre} · ${formatRut(p.rut)}`,
                        // El RUT se muestra con puntos, pero se busca igual
                        // escribiéndolo corrido: el label formateado no calza
                        // contra "152111924".
                        keywords: [cleanRut(p.rut)],
                      }))}
                      value={destinatarioRefs as string[]}
                      onChange={(vals) => setDestinatarioRefs(vals)}
                      placeholder={cargandoPersonas ? "Cargando…" : "Busca por nombre o RUT"}
                      searchPlaceholder="Nombre o RUT"
                      disabled={cargandoPersonas || enviando}
                    />
                  </div>
                )}

                {/* Un solo aviso de excluidos para los cuatro alcances, en
                    gris y no en ámbar: el ámbar de este drawer ya lo gastó la
                    advertencia de que el envío es irreversible, y dos bloques
                    del mismo peso aplanan esa jerarquía. El detalle
                    desplegable se conserva — es la diferencia entre saber que
                    faltan dos y saber QUIÉNES faltan. */}
                {excluidos && hayExcluidos && (
                  <div className="flex items-start gap-2">
                    <Icon name="info" size={15} className="text-icon-soft mt-px shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-soft text-[11.5px] leading-[1.45]">
                        {destinatarioTipo === "persona"
                          ? excluidos.total === 1
                            ? "1 trabajador no aparece en esta lista"
                            : `${excluidos.total} trabajadores no aparecen en esta lista`
                          : excluidos.total === 1
                            ? "1 trabajador no recibirá esta capacitación"
                            : `${excluidos.total} trabajadores no recibirán esta capacitación`}
                        : solo se envía a trabajadores activos y con correo registrado.
                      </p>
                      <button
                        type="button"
                        onClick={() => setVerExcluidos((v) => !v)}
                        aria-expanded={verExcluidos}
                        className="text-soft mt-1 flex items-center gap-0.5 text-[11.5px] font-medium hover:underline"
                      >
                        <Icon name={verExcluidos ? "expand_less" : "expand_more"} size={15} />
                        {verExcluidos ? "Ocultar detalle" : "Ver detalle"}
                      </button>

                      {verExcluidos && (
                        <ul className="border-border-subtle mt-2 flex flex-col gap-1 border-t pt-2">
                          {excluidos.personas.map((p) => (
                            <li
                              key={p.uuid}
                              className="flex items-baseline justify-between gap-2 text-[11.5px] leading-[1.5]"
                            >
                              <span className="truncate text-muted-foreground">{p.nombre}</span>
                              {/* Rótulo de ESTADO, no adjetivo: mismo término
                                  que ya usa el listado de usuarios, y así no
                                  hay que concordar género con la persona. */}
                              <span className="text-soft shrink-0">
                                {p.motivo === "inactivo" ? "Inactivo" : "Sin correo"}
                              </span>
                            </li>
                          ))}
                          {excluidos.total > excluidos.personas.length && (
                            // El backend capa la lista en 25 pero `total` es
                            // exacto, así que el resto se cuenta contra `total`.
                            <li className="text-soft pt-0.5 text-[11.5px]">
                              y {excluidos.total - excluidos.personas.length} más
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </SeccionDrawer>

          <div className="bg-primary-50 border-primary-200 flex flex-col gap-2.5 rounded-lg border p-3.5">
            <div className="flex items-center gap-2.5">
              <Icon name="groups" size={19} className="text-primary-hover shrink-0" />
              <div className="flex-1">
                <div className="text-[12px] text-muted-foreground">Llega a</div>
                <div className="fc-num text-[18px] font-semibold">{nDestinatarios} trabajadores</div>
              </div>
              <div className="text-right">
                <div className="text-[12px] text-muted-foreground">Preguntas por temática</div>
                <div className="fc-num text-[18px] font-semibold">{preguntas}</div>
              </div>
            </div>
            <div className="border-primary-200 border-t" />
            <p className="text-[11.5px] leading-[1.5] text-muted-foreground">{resumenEnvio}</p>
          </div>
        </SheetBody>

        <SheetFooter>
          <span className="text-soft flex-1 text-xs">{nDestinatarios} destinatarios</span>
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Button>
          <Button size="sm" onClick={confirmar} disabled={enviando}>
            <Icon name="send" size={16} />
            {enviando ? "Enviando…" : "Enviar ahora"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
